import React, { useState, useEffect, useCallback } from 'react';
import {
  Home, CreditCard, Wrench, FileText, ChevronRight, Clock,
  Plus, CheckCircle2, Download, MessageCircle, ShieldCheck, X, Send,
  CalendarDays, ArrowRight, AlertCircle, Eye, MessagesSquare,
} from 'lucide-react';
import { theme, isStaffEmail } from '../shared/theme';
import { PortalHeader, PortalBottomNav, NavItem } from '../shared/PortalChrome';
import { portalFetch } from '../shared/api';
import { useAutoRefresh } from '../shared/useAutoRefresh';
import { useCountUp } from '../shared/useCountUp';
import EAAdminConsole from '../ea/EAAdminConsole';
import CustomerMessages from '../shared/CustomerMessages';
import RoleSwitch from '../shared/RoleSwitch';
import { tenantChannel, TENANT_TOKEN_KEY, pollWhileActive } from '../shared/messaging';
import { useLanguage } from '../../components/LanguageContext';
import {
  TENANT, TENANT_RENT, TENANT_DOCS, TENANT_COMPLAINTS, COMPLAINT_CATEGORIES,
  Complaint,
} from '../shared/mockData';
import { isFilmMode } from '../demo/demoMode';
import { openWhatsApp, WHATSAPP_TENANT_NUMBER } from '../shared/contact';
import '../shared/portals.css';

// ============================================================
// TenantPortal — Overview · Payments · Requests · Documents.
// Loads the REAL connected account from the local dev API (which
// reads the Firebase emulator). Falls back to mock data if the dev
// API isn't running, so the UI never breaks.
// ============================================================

// The portal reads gold-over-ink, like the Owner side and the brand: a tenant
// and an owner are looking at the same company. Gold is for marks and
// emphasis only — at 1.9:1 on white it can never carry small text, so labels
// and links take ink, and filled buttons take ink with white on top.
const ACCENT = theme.gold;          // marks, rules, progress
const ACCENT_DEEP = '#B99A4C';      // gold dark enough to read as an icon glyph
const ACCENT_TEXT = theme.navy;     // anything that has to be legible as text
const DEFAULT_IMG =
  'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?auto=format&fit=crop&w=1200&q=60';

interface Props {
  onChangeRole?: () => void;
  /**
   * Owner | Tenant switch on the sign-in screen. The app opens on
   * WelcomeScreen and continues as an owner, so this is how someone who is
   * actually a tenant corrects that — and how a tenant who landed here goes
   * back to the owner login without leaving the sign-in. See RoleSwitch.
   */
  onSwitchRole?: (role: 'owner' | 'tenant') => void;
  // When set (e.g. EA "view as"), skip the login screen and load this
  // tenant's account directly.
  previewEmail?: string;
  /**
   * Code-gated showcase (portals/demo/DemoShell): skip the login screen and
   * stay on MOCK_VM, which is already a complete sample tenancy. No email is
   * involved, so nothing identifies a real tenant. API traffic is blocked at
   * window.fetch by the shell, so the auto-refresh below cannot reach the
   * backend either.
   */
  demoMode?: boolean;
}

// `label` holds a translation key; resolved with t() where the nav is rendered.
const NAV: NavItem[] = [
  { id: 'overview', label: 'home', icon: Home },
  { id: 'payments', label: 'payments', icon: CreditCard },
  // Talking to the management team is not a maintenance request. Requests
  // are jobs with a category and a status; this is the question you ask
  // before, during or instead of raising one.
  { id: 'messages', label: 'omTitle', icon: MessagesSquare },
  { id: 'requests', label: 'requests', icon: Wrench },
  { id: 'documents', label: 'documents', icon: FileText },
];

// ---- normalized view model shared by real + mock ----
interface RentRowVM { chequeNo: string; amount: number; dueDate: string; paid: boolean; status: string; }
interface DocVM { id: string; name: string; type: string; date: string; }
interface VM {
  tenantId: string;
  tenantName: string;
  unitName: string;
  community: string;
  contractStart: string;
  contractEnd: string;
  annualRent: number;
  paidToDate: number;
  nextAmount: number;
  nextDate: string;
  nextDateRaw: string;
  contractEndRaw: string;
  image: string;
  rent: RentRowVM[];
  documents: DocVM[];
  complaints: Complaint[];
  source: 'live' | 'mock';
}

const APPROVED = (s: string) => s === 'approved' || s === 'paid';

function buildVMFromApi(b: any, tr: (k: string) => string): VM {
  const t = b.tenant || {};
  const u = b.unit || {};
  const rentRaw: any[] = u.rentSchedule || [];
  const rent: RentRowVM[] = rentRaw.map((r) => ({
    chequeNo: r.chequeNo, amount: r.amount, dueDate: r.dueDate,
    paid: APPROVED(r.status), status: r.status,
  }));
  const paidToDate = rent.filter((r) => r.paid).reduce((s, r) => s + r.amount, 0);
  const next = rent.find((r) => !r.paid);
  return {
    tenantId: t.id,
    tenantName: t.name || tr('tpTenant'),
    unitName: u.name || t.unitId || tr('tpYourUnit'),
    community: u.community || u.location || '',
    contractStart: fmt(t.contractStart),
    contractEnd: fmt(t.contractEnd),
    annualRent: t.annualRent || u.annualRent || 0,
    paidToDate,
    nextAmount: next ? next.amount : 0,
    nextDate: next ? fmt(next.dueDate) : '—',
    nextDateRaw: next ? next.dueDate : '',
    contractEndRaw: t.contractEnd || '',
    image: DEFAULT_IMG,
    rent,
    documents: (b.documents || []).map((d: any, i: number) => ({
      id: d.id || `d${i}`, name: d.name, type: d.documentType || d.type || tr('tpDocument'), date: fmt(d.date) || '',
    })),
    complaints: (b.complaints || []).map((c: any) => ({
      id: c.id, category: c.category, title: c.title, detail: c.detail,
      status: c.status, createdAt: fmt(c.createdAt) || tr('tpRecently'),
    })),
    source: 'live',
  };
}

const MOCK_VM: VM = {
  tenantId: TENANT.id,
  tenantName: TENANT.name,
  unitName: TENANT.unit,
  community: TENANT.community,
  contractStart: TENANT.contractStart,
  contractEnd: TENANT.contractEnd,
  annualRent: TENANT.annualRent,
  paidToDate: TENANT.paidToDate,
  nextAmount: TENANT.nextPaymentAmount,
  nextDate: TENANT.nextPaymentDate,
  nextDateRaw: TENANT.nextPaymentDate,
  contractEndRaw: TENANT.contractEnd,
  image: TENANT.image,
  // The real four cheques, not a generated series: the old version invented
  // 2025 due dates that aged into the past and showed the sample tenancy as
  // overdue. These are the same cheques the owner sees.
  rent: TENANT_RENT.map((c) => ({ ...c })),
  documents: TENANT_DOCS.map((d) => ({ id: d.id, name: d.name, type: d.type, date: d.date })),
  complaints: TENANT_COMPLAINTS,
  source: 'mock',
};

function fmt(d?: any): string {
  if (!d) return '';
  // Handle Firestore Timestamps ({ _seconds } / { seconds }) as well as
  // ISO strings — never return a non-string (would crash React rendering).
  if (typeof d === 'object') {
    const secs = d._seconds ?? d.seconds;
    if (typeof secs === 'number') d = new Date(secs * 1000);
    else return '';
  }
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return typeof d === 'string' ? d : '';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const TenantPortal: React.FC<Props> = ({ onChangeRole, onSwitchRole, previewEmail, demoMode }) => {
  const { t } = useLanguage();
  // Showcase starts already "signed in" on the sample tenancy.
  const [authed, setAuthed] = useState(!!demoMode);
  const [staff, setStaff] = useState(false);
  const [tab, setTab] = useState('overview');
  const [vm, setVm] = useState<VM>(MOCK_VM);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Remember which account is signed in so we can silently re-read it.
  const [email, setEmail] = useState('');
  // Unread replies from the management team, for the badge on the nav.
  const [unreadMessages, setUnreadMessages] = useState(0);

  // The token minted at OTP login (Backend/pmRoutes). Held in localStorage
  // rather than state because portalFetch reads it there, and because a
  // tenant who closes the app should not need a fresh code to read a reply.
  const keepToken = (bundle: any) => {
    try {
      if (bundle && bundle.token) localStorage.setItem(TENANT_TOKEN_KEY, bundle.token);
      else localStorage.removeItem(TENANT_TOKEN_KEY);
    } catch { /* private mode — messaging will just ask them to sign in again */ }
  };

  const handleLogin = async (loginEmail: string) => {
    if (isStaffEmail(loginEmail)) { setStaff(true); setAuthed(true); return; }
    setEmail(loginEmail);
    setLoading(true);
    try {
      const bundle = await portalFetch<any>('/tenant/login', {
        method: 'POST', body: JSON.stringify({ email: loginEmail }),
      });
      keepToken(bundle);
      setVm(buildVMFromApi(bundle, t));
    } catch {
      setVm(MOCK_VM); // dev API not running → mock
    } finally {
      setLoading(false);
      setAuthed(true);
    }
  };

  // Silently re-read the account so admin updates (rent, documents,
  // complaints) reflect without returning to the login screen.
  const refresh = useCallback(async () => {
    if (!email) return;
    setRefreshing(true);
    try {
      const bundle = await portalFetch<any>('/tenant/login', {
        method: 'POST', body: JSON.stringify({ email }),
      });
      setVm(buildVMFromApi(bundle, t));
    } catch {
      /* keep current data if the dev API is momentarily unavailable */
    } finally {
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // EA "view as" / preview: skip the login screen and load directly.
  useEffect(() => {
    if (previewEmail && !authed) handleLogin(previewEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewEmail]);

  useAutoRefresh(refresh, { intervalMs: 30000, enabled: authed && !staff && vm.source === 'live' });

  // The unread badge on the Messages tab, polled from wherever the tenant is
  // standing. Without this it was only ever set by the Messages screen
  // itself — which meant the badge appeared exactly when it was no longer
  // needed, because opening the thread is what marks it read. A tenant
  // sitting on Overview never learned that a reply had arrived.
  useEffect(() => {
    if (!authed || staff || demoMode) return;
    let alive = true;
    const poll = () => {
      tenantChannel.summary()
        .then((r) => { if (alive) setUnreadMessages(r.unread || 0); })
        .catch(() => { /* badge only — never worth an error on screen */ });
    };
    poll();
    const stop = pollWhileActive(poll, 60000);
    return () => { alive = false; stop(); };
  }, [authed, staff, demoMode]);

  if (!authed) return (
    <TenantLogin
      onVerified={(bundle, e) => { keepToken(bundle); setEmail(e); setVm(buildVMFromApi(bundle, t)); setAuthed(true); }}
      onStaff={() => { setStaff(true); setAuthed(true); }}
      onChangeRole={onChangeRole}
      onSwitchRole={onSwitchRole}
    />
  );

  if (staff) {
    return <EAAdminConsole staffName={t('tpStaff')} roleLabel={t('tpTenantWorkspace')} onExit={onChangeRole} />;
  }

  const submitComplaint = async (c: Complaint) => {
    // Optimistic add + persist to dev API (owner NOT notified).
    setVm((prev) => ({ ...prev, complaints: [c, ...prev.complaints] }));
    if (vm.source === 'live') {
      try {
        await portalFetch(`/tenant/${vm.tenantId}/complaints`, {
          method: 'POST',
          body: JSON.stringify({ category: c.category, title: c.title, detail: c.detail }),
        });
      } catch { /* keep optimistic */ }
    }
  };

  const titles: Record<string, string> = {
    overview: t('tpMyHome'), payments: t('payments'), requests: t('requests'),
    documents: t('documents'), messages: t('omTitle'),
  };

  return (
    <div className="pt-screen">
      <PortalHeader
        title={titles[tab]}
        subtitle={vm.source === 'mock' && !isFilmMode() ? t('tpPreviewData') : undefined}
        accent={ACCENT_DEEP}
        onRefresh={email ? refresh : undefined}
        refreshing={refreshing}
        onChangeRole={onChangeRole}
      />
      <div className="pt-fade">
        {tab === 'messages' && (
          <CustomerMessages
            channel={tenantChannel}
            customerName={vm.tenantName}
            demoMode={demoMode}
            onUnreadChange={setUnreadMessages}
          />
        )}
        {tab === 'overview' && <Overview vm={vm} onGoto={setTab} />}
        {tab === 'payments' && <Payments vm={vm} />}
        {tab === 'requests' && <Requests vm={vm} onSubmit={submitComplaint} />}
        {tab === 'documents' && <Documents vm={vm} />}
      </div>
      <PortalBottomNav
        items={NAV.map((n) => ({ ...n, label: t(n.label), badge: n.id === 'messages' ? unreadMessages : undefined }))}
        active={tab}
        onChange={setTab}
        accent={ACCENT_DEEP}
      />
    </div>
  );
};

// ---------------- Login ----------------
const TenantLogin: React.FC<{
  onVerified: (bundle: any, email: string) => void;
  onStaff: (email: string) => void;
  onChangeRole?: () => void;
  onSwitchRole?: (role: 'owner' | 'tenant') => void;
}> = ({ onVerified, onStaff, onChangeRole, onSwitchRole }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const sendCode = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { setErr(t('tpEnterEmail')); return; }
    if (isStaffEmail(e)) { onStaff(e); return; }
    setErr(''); setBusy(true);
    try {
      await portalFetch('/tenant/request-otp', { method: 'POST', body: JSON.stringify({ email: e }) });
      setInfo(`${t('tpCodeSentTo')} ${e}`); setStep('code');
    } catch (ex: any) {
      setErr(ex?.message || t('tpCodeSendFailed'));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { setErr(t('tpEnterCode')); return; }
    setErr(''); setBusy(true);
    try {
      const bundle = await portalFetch<any>('/tenant/verify-otp', { method: 'POST', body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim() }) });
      onVerified(bundle, email.trim().toLowerCase());
    } catch (ex: any) {
      setErr(ex?.message || t('tpCodeInvalid'));
    } finally { setBusy(false); }
  };

  const onKey = (fn: () => void) => (e: React.KeyboardEvent) => { if (e.key === 'Enter') fn(); };

  return (
    <div className="pt-screen pl-screen">
      <div className="pl-frame" aria-hidden="true" />

      <div className="pl-content">
        <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />

        <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>

        <div className="pl-eyebrow">{t('tpAccessEyebrow')}</div>
        <h1 className="pl-title">{step === 'email' ? t('tpLoginTitle') : t('tpCheckInbox')}</h1>
        <p className="pl-sub">
          {step === 'email' ? t('tpLoginSub') : t('tpCodeSub')}
        </p>

        <div className="pl-sheet">
          {/* Who you are, asked on the sign-in itself. Hidden once the code has
              been sent: switching role there would silently abandon a live OTP. */}
          {onSwitchRole && step === 'email' && (
            <RoleSwitch value="tenant" onChange={onSwitchRole} />
          )}

          {step === 'email' ? (
            <>
              <label className="pl-label" htmlFor="tenant-email">{t('email')}</label>
              <input
                id="tenant-email"
                className="pl-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={onKey(sendCode)}
                placeholder="you@email.com"
              />
              <button onClick={sendCode} disabled={busy} className="pl-btn">
                {busy ? <span className="pt-spin" /> : t('tpSendCode')}
              </button>
            </>
          ) : (
            <>
              <label className="pl-label" htmlFor="tenant-code">{t('tpCodeLabel')}</label>
              <input
                id="tenant-code"
                className="pl-input pl-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={onKey(verify)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
              />
              <button onClick={verify} disabled={busy} className="pl-btn">
                {busy ? <span className="pt-spin" /> : t('tpVerifySignIn')}
              </button>
              <div className="pl-links">
                <button className="pl-link" onClick={() => { setStep('email'); setCode(''); setErr(''); setInfo(''); }}>{t('tpChangeEmail')}</button>
                <button className="pl-link" onClick={sendCode} disabled={busy}>{t('tpResendCode')}</button>
              </div>
            </>
          )}

          {info && !err && <p className="pl-note">{info}</p>}
          {err && <p className="pl-err">{err}</p>}
        </div>

        {onChangeRole && (
          <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
        )}
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%', marginTop: 6, padding: '12px 14px', border: `1px solid ${theme.line}`,
  borderRadius: 12, fontSize: 15, outline: 'none', background: '#f9fafb',
};

// ---------------- Overview ----------------
const MS_DAY = 86400000;
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };
function parseDate(s?: string): number | null {
  if (!s) return null;
  const t = new Date(s).getTime();
  return isNaN(t) ? null : t;
}
// Whole days from today until `raw` (negative = in the past).
function daysUntil(raw?: string): number | null {
  const t = parseDate(raw);
  if (t === null) return null;
  return Math.round((new Date(t).setHours(0, 0, 0, 0) - startOfToday()) / MS_DAY);
}

// Date-aware status for a single cheque/instalment. Unlike a plain
// paid/unpaid flag, this marks a past-due unpaid cheque as Overdue.
// `label` stays English because it is compared in code; `key` is what gets shown.
type ChequeState = { label: string; key: string; color: string; bg: string; icon: React.ComponentType<any> };
function chequeState(r: RentRowVM): ChequeState {
  if (r.paid) return { label: 'Paid', key: 'paid', color: theme.success, bg: theme.successBg, icon: CheckCircle2 };
  // Handed over and sitting with the owner or EA for confirmation — the
  // statuses the PM module sets on a cheque (see the rent status endpoint in
  // dev-api-server). The ball is not in the tenant's court, so this must never
  // fall through to Overdue: it would chase someone for money they have
  // already paid, and the sample tenancy showed exactly that.
  const st = (r.status || '').toLowerCase();
  if (st === 'awaiting_approval' || st === 'approved' || st === 'submitted') {
    return { label: 'Submitted', key: 'tpChequeSubmitted', color: theme.gold, bg: 'rgba(214,177,96,0.14)', icon: Clock };
  }
  const d = daysUntil(r.dueDate);
  if (d !== null && d < 0) return { label: 'Overdue', key: 'overdueLabel', color: theme.danger, bg: theme.dangerBg, icon: AlertCircle };
  if (d === 0) return { label: 'Due today', key: 'tpDueToday', color: theme.warning, bg: theme.warningBg, icon: Clock };
  return { label: 'Upcoming', key: 'upcomingLabel', color: theme.warning, bg: theme.warningBg, icon: Clock };
}

// ---------------- Lease renewal ----------------
// Every date here is derived from the contract itself: Dubai practice is that
// either side must give notice 90 days before a tenancy ends, and a tenant who
// finds that out late has lost the choice. So the card counts down to the
// notice window rather than to the end date, and marks each step done as it
// passes. Nothing here is a mock-up — remove the lease dates and it hides.
const NOTICE_DAYS = 90;

const addDays = (iso: string | undefined, days: number): Date | null => {
  const t = parseDate(iso);
  if (t === null) return null;
  const d = new Date(t);
  d.setDate(d.getDate() + days);
  return d;
};

const RenewalCard: React.FC<{ vm: VM }> = ({ vm }) => {
  const { t } = useLanguage();
  const dEnd = daysUntil(vm.contractEndRaw);
  if (dEnd === null || dEnd < 0) return null;

  const noticeDate = addDays(vm.contractEndRaw, -NOTICE_DAYS);
  const dNotice = noticeDate ? Math.round((noticeDate.setHours(0, 0, 0, 0) - startOfToday()) / MS_DAY) : null;
  const newStart = addDays(vm.contractEndRaw, 1);

  const left = dEnd < 60 ? `${dEnd} ${t('tpDaysLeft')}` : `${Math.round(dEnd / 30)} ${t('tpMonthsLeft')}`;

  const steps = [
    {
      label: t('tpRenewalStepNotice'),
      sub: t('tpRenewalStepNoticeSub'),
      date: noticeDate,
      done: dNotice !== null && dNotice <= 0,
      now: dNotice !== null && dNotice <= 0,
    },
    {
      label: t('tpRenewalStepDecide'),
      sub: t('tpRenewalStepDecideSub'),
      date: parseDate(vm.contractEndRaw) ? new Date(parseDate(vm.contractEndRaw)!) : null,
      done: false,
      now: false,
    },
    {
      label: t('tpRenewalStepNew'),
      sub: t('tpRenewalStepNewSub'),
      date: newStart,
      done: false,
      now: false,
    },
  ];

  return (
    <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `${ACCENT}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <CalendarDays size={17} color={ACCENT_DEEP} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{t('tpRenewalTitle')}</div>
          <div style={{ fontSize: 12, color: theme.slate }}>
            {t('tpRenewalEndsIn')} <strong style={{ color: ACCENT_TEXT }}>{left}</strong> · {t('tpRenewalEndsOn')} {vm.contractEnd}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        {steps.map((s, i) => (
          <div key={s.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {/* rail */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'stretch' }}>
              <div style={{
                width: 11, height: 11, borderRadius: 99, marginTop: 4, flexShrink: 0,
                background: s.done ? ACCENT : '#fff',
                border: `2px solid ${s.done ? ACCENT : theme.line}`,
              }} />
              {i < steps.length - 1 && (
                <div style={{ width: 2, flex: 1, minHeight: 26, background: s.done ? ACCENT : theme.line }} />
              )}
            </div>
            <div style={{ paddingBottom: i < steps.length - 1 ? 14 : 0, flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: theme.ink }}>{s.label}</span>
                {s.now && (
                  <span style={{ ...pill(`${ACCENT}26`, '#7a6222'), fontSize: 10.5 }}>{t('tpRenewalOpenNow')}</span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: theme.slate, marginTop: 2, lineHeight: 1.4 }}>{s.sub}</div>
              {s.date && (
                <div style={{ fontSize: 12, fontWeight: 600, color: ACCENT_TEXT, marginTop: 3 }}>
                  {s.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => openWhatsApp(
          `Hello, this is ${vm.tenantName} (${vm.unitName}). I would like to talk about renewing my contract, which ends ${vm.contractEnd}.`,
          WHATSAPP_TENANT_NUMBER,
        )}
        className="pt-tap"
        style={{
          width: '100%', marginTop: 16, padding: 12,
          background: `linear-gradient(135deg, ${theme.navy}, #16213e)`, color: '#fff',
          border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        <MessageCircle size={16} /> {t('tpRenewalTalk')}
      </button>
    </div>
  );
};

// ---------------- Document preview ----------------
// The fixtures carry no files, and a download that does nothing is worse on a
// tenant's phone than no button at all. This renders the document the portal
// already knows about — labelled a sample, so it can never be mistaken for the
// executed contract.
const SampleDocModal: React.FC<{ vm: VM; doc: DocVM | null; onClose: () => void }> = ({ vm, doc, onClose }) => {
  const { t } = useLanguage();
  if (!doc) return null;

  const row = (label: string, value?: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '9px 0', borderBottom: `1px solid ${theme.line}` }}>
      <span style={{ fontSize: 12, color: theme.slate }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: theme.ink, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(17,24,43,0.6)',
        backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto',
          background: theme.card, borderRadius: '18px 18px 0 0', padding: '16px 16px 22px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: theme.ink }}>{t('tpSampleDocTitle')}</div>
            <div style={{ fontSize: 12, color: theme.slate }}>{doc.type}{doc.date ? ` · ${doc.date}` : ''}</div>
          </div>
          <button onClick={onClose} className="pt-tap" style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer' }}>
            <X size={20} color={theme.slate} />
          </button>
        </div>

        {/* The page */}
        <div style={{ position: 'relative', border: `1px solid ${theme.line}`, borderRadius: 12, padding: 20, background: '#fff', overflow: 'hidden' }}>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transform: 'rotate(-24deg)', fontSize: 42, fontWeight: 800, letterSpacing: 4,
              color: `${ACCENT}2e`, pointerEvents: 'none', textTransform: 'uppercase',
            }}
          >
            {t('tpSampleDocNote')}
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', color: ACCENT_DEEP, textTransform: 'uppercase' }}>
              Asset Desk
            </div>
            <div style={{ height: 2, width: 44, background: ACCENT, margin: '7px 0 14px' }} />
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.ink, marginBottom: 14 }}>{doc.name}</div>

            {row(t('tpSampleDocProperty'), vm.unitName)}
            {row(t('tpSampleDocTenant'), vm.tenantName)}
            {row(t('tpSampleDocPeriod'), `${vm.contractStart} — ${vm.contractEnd}`)}
            {row(t('tpSampleDocRent'), `AED ${(vm.annualRent || 0).toLocaleString('en-AE')}`)}
            {row(t('tpSampleDocEjari'), TENANT.ejariNo)}
          </div>
        </div>

        <button
          onClick={onClose}
          className="pt-tap"
          style={{
            width: '100%', marginTop: 16, padding: 13, borderRadius: 12, border: `1px solid ${theme.line}`,
            background: '#fff', color: theme.ink, fontSize: 14.5, fontWeight: 700, cursor: 'pointer',
          }}
        >
          {t('tpSampleDocClose')}
        </button>
      </div>
    </div>
  );
};

const Overview: React.FC<{ vm: VM; onGoto: (t: string) => void }> = ({ vm, onGoto }) => {
  const { t } = useLanguage();
  const paidPct = vm.annualRent ? Math.min(100, Math.round((vm.paidToDate / vm.annualRent) * 100)) : 0;
  const firstName = (vm.tenantName || 'there').trim().split(/\s+/)[0];
  const nextAmt = useCountUp(vm.nextAmount);

  // Next-payment status.
  const dNext = daysUntil(vm.nextDateRaw);
  let payStatus: { label: string; bg: string; color: string };
  if (paidPct >= 100) payStatus = { label: t('fullyPaid'), bg: 'rgba(255,255,255,0.22)', color: '#fff' };
  else if (dNext !== null && dNext < 0) payStatus = { label: t('overdueLabel'), bg: 'rgba(254,202,202,0.28)', color: '#fee2e2' };
  else if (dNext === 0) payStatus = { label: t('tpDueToday'), bg: 'rgba(255,255,255,0.22)', color: '#fff' };
  else if (dNext !== null) payStatus = { label: `${t('tpDueIn')} ${dNext} ${dNext === 1 ? t('tpDay') : t('tpDaysDat')}`, bg: 'rgba(255,255,255,0.22)', color: '#fff' };
  else payStatus = { label: t('upcomingLabel'), bg: 'rgba(255,255,255,0.22)', color: '#fff' };

  // Next-payment notification banner (due within 30 days or overdue).
  const showPayAlert = paidPct < 100 && dNext !== null && dNext <= 30;
  const payOverdue = dNext !== null && dNext < 0;

  // Lease countdown chip.
  const dLease = daysUntil(vm.contractEndRaw);
  let leaseChip: string | null = null;
  if (dLease !== null) {
    if (dLease <= 0) leaseChip = t('tpLeaseEnded');
    else if (dLease < 60) leaseChip = `${dLease} ${t('tpDaysLeft')}`;
    else leaseChip = `${Math.round(dLease / 30)} ${t('tpMonthsLeft')}`;
  }

  const openRequest = vm.complaints.find((c) => c.status !== 'resolved') || vm.complaints[0];
  const recentDocs = vm.documents.slice(0, 3);

  return (
    <div className="pt-stagger" style={{ padding: '14px 16px 24px' }}>
      {/* Greeting */}
      <div style={{ margin: '2px 2px 14px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: ACCENT_DEEP, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{t('welcomeBack')}</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: theme.ink, marginTop: 2, letterSpacing: '-0.3px' }}>{firstName}</div>
      </div>

      {/* Next-payment notification */}
      {showPayAlert && (
        <div onClick={() => onGoto('payments')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 11, margin: '0 2px 14px', padding: '12px 14px', borderRadius: 14, background: payOverdue ? 'rgba(239,68,68,0.10)' : 'rgba(214,177,96,0.14)', border: `1px solid ${payOverdue ? 'rgba(239,68,68,0.35)' : 'rgba(214,177,96,0.4)'}` }}>
          <div style={{ fontSize: 20, lineHeight: 1 }}>🔔</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: payOverdue ? '#b91c1c' : '#8a6d1f' }}>
              {payOverdue
                ? `${t('tpRentOverdueBy')} ${-dNext!} ${dNext === -1 ? t('tpDay') : t('tpDaysDat')}`
                : dNext === 0
                  ? t('tpRentDueToday')
                  : `${t('tpRentDueIn')} ${dNext} ${dNext === 1 ? t('tpDay') : t('tpDaysDat')}`}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>AED {(vm.nextAmount || 0).toLocaleString()} · {t('tpDueLower')} {vm.nextDate} — {t('tpTapToView')}</div>
          </div>
        </div>
      )}

      {/* Property hero — photo with overlay + lease chip */}
      <div className="pt-card" style={{ overflow: 'hidden', marginBottom: 16, position: 'relative' }}>
        <div style={{ height: 168, background: `url(${vm.image}) center/cover` }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 38%, rgba(0,0,0,0.72) 100%)' }} />
        {leaseChip && (
          <span style={{ position: 'absolute', top: 12, right: 12, fontSize: 11.5, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.42)', backdropFilter: 'blur(6px)', padding: '5px 11px', borderRadius: 99, display: 'flex', alignItems: 'center', gap: 5 }}>
            <CalendarDays size={13} /> {leaseChip}
          </span>
        )}
        <div style={{ position: 'absolute', left: 16, right: 16, bottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>{vm.unitName}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', marginTop: 1 }}>{vm.community}</div>
        </div>
      </div>

      {/* Next payment — accent card */}
      <button
        onClick={() => onGoto('payments')}
        className="pt-card pt-accent tenant-accent pt-tap"
        style={{ width: '100%', padding: 18, marginBottom: 16, border: 'none', textAlign: 'left', display: 'block' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 600 }}>{t('tpNextPaymentDue')}</span>
          <span style={{ ...pill(payStatus.bg, payStatus.color), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} /> {payStatus.label}
          </span>
        </div>
        <div style={{ color: '#fff', fontSize: 30, fontWeight: 800, marginTop: 10, letterSpacing: -0.5 }}>
          AED {nextAmt.toLocaleString('en-AE')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>{vm.nextDate}</span>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {t('tpViewSchedule')} <ArrowRight size={13} />
          </span>
        </div>
      </button>

      {/* Rent paid this year */}
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{t('tpRentPaidThisYear')}</span>
          <span style={{ fontSize: 14.5, fontWeight: 800, color: ACCENT_TEXT }}>{paidPct}%</span>
        </div>
        <div className="pt-progress-track" style={{ marginBottom: 10 }}>
          <div className="pt-progress-fill" style={{ width: `${paidPct}%`, background: `linear-gradient(90deg, ${ACCENT_DEEP}, ${ACCENT})` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: theme.slate }}>
          <span>AED {vm.paidToDate.toLocaleString('en-AE')} {t('tpPaidLower')}</span>
          <span>AED {vm.annualRent.toLocaleString('en-AE')} {t('tpTotalLower')}</span>
        </div>
      </div>

      {/* Recent request */}
      <SectionHeader title={t('tpRecentRequest')} action={openRequest ? t('viewAll') : undefined} onAction={() => onGoto('requests')} />
      <button
        onClick={() => onGoto('requests')}
        className="pt-card pt-tap"
        style={{ width: '100%', padding: 16, marginBottom: 16, border: 'none', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${ACCENT}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Wrench size={19} color={ACCENT_DEEP} />
        </div>
        {openRequest ? (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{openRequest.title}</div>
              <div style={{ fontSize: 12.5, color: theme.slate, marginTop: 2 }}>{openRequest.category} · {openRequest.createdAt}</div>
            </div>
            <ComplaintBadge status={openRequest.status} />
          </>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{t('tpNoOpenRequests')}</div>
              <div style={{ fontSize: 12.5, color: theme.slate, marginTop: 2 }}>{t('tpTapToReport')}</div>
            </div>
            <ChevronRight size={20} color={theme.slateLight} />
          </>
        )}
      </button>

      {/* Latest documents */}
      {recentDocs.length > 0 && (
        <>
          <SectionHeader title={t('tpLatestDocuments')} action={`${t('viewAll')} (${vm.documents.length})`} onAction={() => onGoto('documents')} />
          <div className="pt-card" style={{ padding: 6, marginBottom: 16 }}>
            {recentDocs.map((d, i) => (
              <button
                key={d.id}
                onClick={() => onGoto('documents')}
                className="pt-tap"
                style={{ width: '100%', padding: '11px 10px', display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', borderTop: i === 0 ? 'none' : `1px solid ${theme.line}`, textAlign: 'left' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 9, background: `${ACCENT}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={17} color={ACCENT_DEEP} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: theme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                  <div style={{ fontSize: 11.5, color: theme.slate }}>{d.type}{d.date ? ` · ${d.date}` : ''}</div>
                </div>
                <Download size={17} color={theme.slateLight} />
              </button>
            ))}
          </div>
        </>
      )}

      {/* Lease renewal — the date every tenant needs and nobody remembers. */}
      <RenewalCard vm={vm} />

      {/* Quick actions */}
      <SectionHeader title={t('quickActions')} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <QuickAction icon={Wrench} label={t('tpReportIssue')} onClick={() => onGoto('requests')} />
        <QuickAction icon={FileText} label={t('tpMyDocuments')} onClick={() => onGoto('documents')} />
        <QuickAction icon={CreditCard} label={t('tpPaymentPlan')} onClick={() => onGoto('payments')} />
        {/* WhatsApp rather than a phone call: a tenant reporting a problem
            wants to send a photo of it, and the reply lands in a thread the
            team can pick up. WHATSAPP_TENANT_NUMBER is the tenancy line. */}
        <QuickAction
          icon={MessageCircle}
          label={t('tpContactTeam')}
          onClick={() => openWhatsApp(`Hello, this is ${vm.tenantName} (${vm.unitName}).`, WHATSAPP_TENANT_NUMBER)}
        />
      </div>
    </div>
  );
};

const SectionHeader: React.FC<{ title: string; action?: string; onAction?: () => void }> = ({ title, action, onAction }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 2px 10px' }}>
    <span style={{ fontSize: 15, fontWeight: 800, color: theme.ink }}>{title}</span>
    {action && (
      <button onClick={onAction} style={{ background: 'none', border: 'none', color: ACCENT_TEXT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {action} <ChevronRight size={14} />
      </button>
    )}
  </div>
);

const QuickAction: React.FC<{ icon: React.ComponentType<any>; label: string; onClick: () => void }> = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} className="pt-card pt-tap" style={{ padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10, border: 'none', textAlign: 'left' }}>
    <div style={{ width: 40, height: 40, borderRadius: 10, background: `${ACCENT}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={20} color={ACCENT_DEEP} />
    </div>
    <span style={{ fontSize: 13.5, fontWeight: 600, color: theme.ink }}>{label}</span>
  </button>
);

// ---------------- Payments ----------------
const Payments: React.FC<{ vm: VM }> = ({ vm }) => {
  const { t } = useLanguage();
  const paidPct = vm.annualRent ? Math.min(100, Math.round((vm.paidToDate / vm.annualRent) * 100)) : 0;
  const annual = useCountUp(vm.annualRent);
  const remaining = Math.max(0, vm.annualRent - vm.paidToDate);
  const paidCount = vm.rent.filter((r) => r.paid).length;
  const overdue = vm.rent.filter((r) => chequeState(r).label === 'Overdue');
  const overdueTotal = overdue.reduce((s, r) => s + r.amount, 0);

  return (
    <div style={{ padding: '16px 16px 24px' }}>
      {/* Summary */}
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, color: theme.slate }}>{t('tpAnnualRent')}</span>
          <span style={{ fontSize: 12.5, color: theme.slate }}>{vm.rent.length} {vm.rent.length !== 1 ? t('tpCheques') : t('tpCheque')} · {paidCount} {t('tpPaidLower')}</span>
        </div>
        <div style={{ fontSize: 27, fontWeight: 800, color: theme.ink, marginTop: 2, letterSpacing: -0.5 }}>
          AED {annual.toLocaleString('en-AE')}
        </div>
        <div className="pt-progress-track" style={{ margin: '12px 0 10px' }}>
          <div className="pt-progress-fill" style={{ width: `${paidPct}%`, background: `linear-gradient(90deg, ${ACCENT_DEEP}, ${ACCENT})` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
          <span style={{ color: theme.success, fontWeight: 600 }}>AED {vm.paidToDate.toLocaleString('en-AE')} {t('tpPaidLower')}</span>
          <span style={{ color: theme.slate }}>AED {remaining.toLocaleString('en-AE')} {t('remaining')}</span>
        </div>
      </div>

      {/* Overdue banner */}
      {overdue.length > 0 && (
        <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 12, background: theme.dangerBg, color: theme.danger, marginBottom: 16 }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5, fontWeight: 600 }}>
            {overdue.length} {overdue.length !== 1 ? t('tpPayments') : t('tpPayment')} {t('tpOverdueLower')} · AED {overdueTotal.toLocaleString('en-AE')}. {t('tpContactToSettle')}
          </p>
        </div>
      )}

      <SectionHeader title={t('tpChequeSchedule')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {vm.rent.map((r, i) => {
          const st = chequeState(r);
          const Icon = st.icon;
          return (
            <div key={i} className="pt-card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `3px solid ${st.color}` }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={20} color={st.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink }}>{r.chequeNo || `${t('tpChequeLabel')} ${i + 1}`}</div>
                <div style={{ fontSize: 12.5, color: theme.slate, display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                  <CalendarDays size={12} /> {fmt(r.dueDate)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink }}>AED {r.amount.toLocaleString('en-AE')}</div>
                <span style={{ ...pill(st.bg, st.color), marginTop: 4 }}>{t(st.key)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------- Requests / Complaints ----------------
const Requests: React.FC<{ vm: VM; onSubmit: (c: Complaint) => void }> = ({ vm, onSubmit }) => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  return (
    <div style={{ padding: '16px 16px 24px' }}>
      <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 12, background: theme.infoBg, color: '#1e40af', marginBottom: 16 }}>
        <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
          {t('tpRequestsNote')}
        </p>
      </div>

      <button onClick={() => setShowForm(true)} className="pt-tap" style={{ width: '100%', padding: 14, background: `linear-gradient(135deg, ${theme.navy}, #16213e)`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
        <Plus size={18} /> {t('tpNewRequest')}
      </button>

      <div style={{ fontSize: 14, fontWeight: 700, color: theme.ink, margin: '0 4px 10px' }}>{t('tpMyRequests')}</div>
      {vm.complaints.length === 0 && (
        <p style={{ textAlign: 'center', color: theme.slate, fontSize: 13, padding: 20 }}>{t('tpNoRequests')}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {vm.complaints.map((c) => (
          <div key={c.id} className="pt-card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink }}>{c.title}</div>
                <div style={{ fontSize: 12.5, color: theme.slate, marginTop: 2 }}>{c.category} · {c.createdAt}</div>
              </div>
              <ComplaintBadge status={c.status} />
            </div>
            <p style={{ fontSize: 13, color: theme.slate, marginTop: 8, marginBottom: 0 }}>{c.detail}</p>
          </div>
        ))}
      </div>

      {showForm && (
        <NewRequestSheet onClose={() => setShowForm(false)} onSubmit={(c) => { onSubmit(c); setShowForm(false); }} />
      )}
    </div>
  );
};

const ComplaintBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useLanguage();
  const meta: Record<string, { label: string; c: string; bg: string }> = {
    submitted: { label: t('tpSubmitted'), c: theme.warning, bg: theme.warningBg },
    in_review: { label: t('tpInReview'), c: theme.info, bg: theme.infoBg },
    resolved: { label: t('tpResolved'), c: theme.success, bg: theme.successBg },
  };
  const m = meta[status] || meta.submitted;
  return <span style={pill(m.bg, m.c)}>{m.label}</span>;
};

const NewRequestSheet: React.FC<{ onClose: () => void; onSubmit: (c: Complaint) => void }> = ({ onClose, onSubmit }) => {
  const { t } = useLanguage();
  const [category, setCategory] = useState(COMPLAINT_CATEGORIES[0]);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [sending, setSending] = useState(false);

  const submit = () => {
    if (!title.trim()) return;
    setSending(true);
    setTimeout(() => {
      onSubmit({
        id: 'c' + Math.round(performance.now()),
        category, title: title.trim(), detail: detail.trim(),
        status: 'submitted', createdAt: t('tpJustNow'),
      });
    }, 500);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <div className="pt-fade" style={{ position: 'relative', width: '100%', maxWidth: 640, background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: theme.ink, margin: 0 }}>{t('tpNewRequestTitle')}</h2>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, padding: 8, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <label style={{ fontSize: 13, fontWeight: 600, color: theme.ink }}>{t('category')}</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
          {COMPLAINT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>

        <label style={{ fontSize: 13, fontWeight: 600, color: theme.ink, marginTop: 14, display: 'block' }}>{t('tpTitle')}</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('tpTitlePlaceholder')} style={inputStyle} />

        <label style={{ fontSize: 13, fontWeight: 600, color: theme.ink, marginTop: 14, display: 'block' }}>{t('tpDetails')}</label>
        <textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={t('tpDetailsPlaceholder')} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />

        <button onClick={submit} disabled={sending || !title.trim()} className="pt-tap" style={{ width: '100%', marginTop: 20, padding: 14, background: `linear-gradient(135deg, ${theme.navy}, #16213e)`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: !title.trim() ? 0.5 : 1 }}>
          {sending ? <span className="pt-spin" /> : <><Send size={16} /> {t('tpSubmitToTeam')}</>}
        </button>
      </div>
    </div>
  );
};

// ---------------- Documents ----------------
const Documents: React.FC<{ vm: VM }> = ({ vm }) => {
  const { t } = useLanguage();
  const [preview, setPreview] = useState<DocVM | null>(null);
  return (
  <div style={{ padding: '16px 16px 24px' }}>
    {vm.documents.length === 0 && (
      <p style={{ textAlign: 'center', color: theme.slate, fontSize: 13, padding: 20 }}>{t('noDocuments')}</p>
    )}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {vm.documents.map((d) => (
        <button
          key={d.id}
          onClick={() => setPreview(d)}
          className="pt-card pt-tap"
          style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, border: 'none', textAlign: 'left', width: '100%' }}
        >
          <div style={{ width: 42, height: 42, borderRadius: 10, background: `${ACCENT}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={20} color={ACCENT_DEEP} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{d.name}</div>
            <div style={{ fontSize: 12, color: theme.slate }}>{d.type}{d.date ? ` · ${d.date}` : ''}</div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 9, background: `${ACCENT}1f`, color: ACCENT_TEXT, fontSize: 12.5, fontWeight: 700 }}>
            <Eye size={15} /> {t('tpRenewalDoc')}
          </span>
          <Download size={19} color={theme.slateLight} />
        </button>
      ))}
    </div>
    <button
      onClick={() => openWhatsApp(`Hello, this is ${vm.tenantName} (${vm.unitName}). Could you send me`, WHATSAPP_TENANT_NUMBER)}
      className="pt-card pt-tap" style={{ width: '100%', marginTop: 18, padding: 16, display: 'flex', alignItems: 'center', gap: 12, border: 'none' }}>
      <div style={{ width: 42, height: 42, borderRadius: 10, background: '#25D36614', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MessageCircle size={20} color="#25D366" />
      </div>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{t('tpNeedAnotherDoc')}</div>
        <div style={{ fontSize: 12, color: theme.slate }}>{t('tpMessageTeam')}</div>
      </div>
      <ChevronRight size={20} color={theme.slateLight} />
    </button>

    <SampleDocModal vm={vm} doc={preview} onClose={() => setPreview(null)} />
  </div>
  );
};

function pill(bg: string, color: string): React.CSSProperties {
  return { display: 'inline-block', fontSize: 11, fontWeight: 700, color, background: bg, padding: '3px 9px', borderRadius: 99 };
}

export default TenantPortal;
